import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, Quote, Target, Eye, Heart, Globe,
  Sparkles, Users, Award, Lightbulb
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const FounderPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                <Heart className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm font-medium text-[#1E3A5F]">Our Story</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
                Bridging Commerce and
                <span className="block text-[#FDB813]">Consciousness</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                CNESS Works was founded by <strong>Adhipen Bose Nandhiji</strong> to bridge 
                the gap between commerce and consciousness. As a social innovator and entrepreneur, 
                he recognised that many businesses aspire to do good but lack the structure to prove it.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                His mission is to make responsible business practices tangible, measurable and scalable. 
                The CNESS framework reflects years of research into conscious leadership, human-centric 
                culture and sustainable value creation.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] rounded-3xl p-8 text-white">
                <Quote className="w-12 h-12 text-[#FDB813] mb-6 opacity-50" />
                <p className="text-xl leading-relaxed mb-6 italic">
                  "Business can be both profitable and principled. Our journey proves that 
                  conscious enterprise is not a lofty ideal but a practical pathway to long-term success."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#FDB813] rounded-full flex items-center justify-center text-[#1E3A5F] text-2xl font-bold">
                    AN
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Adhipen Bose Nandhiji</p>
                    <p className="text-white/70">Founder & Visionary, CNESS</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Vision */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-3xl border border-blue-100">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                A global marketplace where doing good is rewarded, trust is the currency of commerce, 
                and conscious enterprises set the standard for success.
              </p>
            </div>
            
            {/* Mission */}
            <div className="bg-gradient-to-br from-yellow-50 to-white p-8 rounded-3xl border border-yellow-100">
              <div className="w-14 h-14 bg-yellow-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#FDB813]" />
              </div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To empower organisations to embed consciousness into every decision, align their teams 
                around shared values, and demonstrate their impact with confidence.
              </p>
            </div>
            
            {/* Story */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-3xl border border-green-100">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Our Story</h3>
              <p className="text-gray-600 leading-relaxed">
                From early experiments in social entrepreneurship to building a global community of 
                conscious leaders, our journey proves that business can be both profitable and principled.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Belief Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">
              What If Growth Could Be Measured by Awareness?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              The systems of the past, driven by scale and capital, lost their soul. CNESS is pioneering 
              the next era of advancement: one built on <strong>better, not just bigger</strong>, systems.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We make consciousness measurable and growth intentional. Humanity needs an upgrade.
            </p>
          </div>
        </div>
      </section>

      {/* The Architecture */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              One Ecosystem. Four Interconnected Products.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              CNESS is built as a conscious infrastructure stack — modular, interoperable, and designed for scale.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                name: "CNESS Works", 
                desc: "The conscious backbone of modern enterprise — certifying culture, training teams, and measuring impact.",
                icon: Award,
                color: "#3B82F6"
              },
              { 
                name: "CNESS Life", 
                desc: "A digital home for your conscious identity — certification, learning, and commerce, all in one space.",
                icon: Users,
                color: "#8B5CF6"
              },
              { 
                name: "Super App", 
                desc: "Your essential system for intentional growth, bridging best practices, community, and a marketplace.",
                icon: Sparkles,
                color: "#10B981"
              },
              { 
                name: "AriOme", 
                desc: "A personal betterment engine — daily reflection, guided journeys, and measurable inner growth.",
                icon: Lightbulb,
                color: "#F59E0B"
              }
            ].map((product, index) => (
              <div key={index} className="bg-[#F8FAFC] p-6 rounded-2xl hover:shadow-lg transition-shadow">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${product.color}15` }}
                >
                  <product.icon className="w-6 h-6" style={{ color: product.color }} />
                </div>
                <h4 className="font-semibold text-[#1E3A5F] mb-2">{product.name}</h4>
                <p className="text-sm text-gray-600">{product.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Moment */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              The World Is Ready for Systems That Care
            </h2>
            <div className="grid sm:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#FDB813] mb-2">70%</div>
                <p className="text-white/70">of new consumers prefer brands that align with their values</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#FDB813] mb-2">$20B+</div>
                <p className="text-white/70">ESG/DEI spending projected growth</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#FDB813] mb-2">81%</div>
                <p className="text-white/70">of customers say trust drives decisions</p>
              </div>
            </div>
            <p className="text-lg text-white/80">
              Organizations need to show their impact, not just claim it. People want tools that link 
              profit to purpose. CNESS addresses this need with technology that turns awareness into 
              a measurable benefit.
            </p>
          </div>
        </div>
      </section>

      {/* Proof Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              From Vision to Validation
            </h2>
            <p className="text-lg text-gray-600">Our platform is already in motion.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "4 MVPs", desc: "shipping between 2025 and 2026" },
              { value: "100+", desc: "organizations in certification pipeline" },
              { value: "Global", desc: "council of mentors, investors, and advisors" },
              { value: "Multi-sector", desc: "partnerships across education, impact, and innovation" }
            ].map((stat, index) => (
              <div key={index} className="bg-[#F8FAFC] p-6 rounded-2xl text-center">
                <div className="text-2xl font-bold text-[#1E3A5F] mb-2">{stat.value}</div>
                <p className="text-sm text-gray-600">{stat.desc}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-lg text-gray-600 mt-12">
            We're building not just a product, but a <strong>global category</strong>.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">
            Help Build the Conscious Future
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
            CNESS is for those who believe technology can elevate humanity. For investors who see 
            beyond trends, partners who think bigger than business, and individuals who know 
            consciousness is the real frontier. It's not just a company; it's a movement.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/certification')}
              className="w-full sm:w-auto px-8 py-4 text-lg bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl"
            >
              Join the Movement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="w-full sm:w-auto px-8 py-4 text-lg border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FounderPage;
